import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Pagination,
  Stack,
  Checkbox
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import { statusMapping } from '../services/api.service';
import equipmentService from '../services/equipment.service';
import InventorySummaryMobile from './InventorySummaryMobile';
import StatusHistoryDialog from './StatusHistoryDialog';
import LinkifiedText from './LinkifiedText';
import api from '../services/api.service';
import { printSingleQRCode } from '../services/qrCodePrintService';
import FeatureGate from './FeatureGate';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { decodeEquipmentItem, decodeHTMLEntities } from '../utils/htmlDecoder';
import { getStatusDisplay } from '../constants/equipmentStatus';

// Debug logging utilities - only log in development
const debug = (...args) => {
  if (import.meta.env.DEV) {
  }
};

const debugError = (...args) => {
  if (import.meta.env.DEV) {
    console.error(...args);
  }
};

const debugWarn = (...args) => {
  if (import.meta.env.DEV) {
    console.warn(...args);
  }
};

/**
 * Inventory Summary component for displaying real-time inventory data
 * from Google Sheets.
 */
export default function InventorySummary() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  const { isFeatureEnabled } = useFeatureFlags();
  
  const { 
    getSheetData, 
    searchInventory, 
    loading, 
    error, 
    clearError,
    inventorySummary, // Use the summary from context if available
    manualInventoryItems, // Get the manually added items
    loadInventorySummary, // Get the function to load the inventory summary
    lastUpdateTime // Get the timestamp to detect updates
  } = useInventory();
  
  // Get shared warehouse/rack/bay data from context
  const {
    warehouses: contextWarehouses,
    racks: contextRacks,
    bays: contextBays
  } = useInventoryPage();
  
  // Load saved settings from localStorage
  const loadInventorySettings = () => {
    const savedSettings = localStorage.getItem('inventorySummarySettings');
    if (savedSettings) {
      try {
        return JSON.parse(savedSettings);
      } catch (e) {
        console.error('Failed to parse saved inventory settings:', e);
      }
    }
    return null;
  };

  const savedSettings = loadInventorySettings();

  const [inventoryData, setInventoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [warehouse, setWarehouse] = useState(savedSettings?.warehouse || 'All');
  const [searchTerm, setSearchTerm] = useState(savedSettings?.searchTerm || '');
  const [isLoading, setIsLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [warehouses, setWarehouses] = useState(['All']);
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [allBays, setAllBays] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showRefreshMessage, setShowRefreshMessage] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(savedSettings?.autoRefresh ?? false); // Turn off auto-refresh by default
  const [showArchived, setShowArchived] = useState(savedSettings?.showArchived ?? false); // Toggle to show archived items
  const [showAvailableOnly, setShowAvailableOnly] = useState(savedSettings?.showAvailableOnly ?? false); // Toggle to show only groups with at least one available item

  // Category filter state
  const [selectedCategory, setSelectedCategory] = useState(savedSettings?.selectedCategory || 'All');
  const [categories, setCategories] = useState([]);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editBays, setEditBays] = useState([]);
  
  // Sorting state
  const [sortField, setSortField] = useState(savedSettings?.sortField || '');
  const [sortDirection, setSortDirection] = useState(savedSettings?.sortDirection || 'asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(savedSettings?.itemsPerPage || 50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Grouping and expansion state
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [groupedData, setGroupedData] = useState([]);

  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState([]);

  // Helper functions to map UUIDs to names using loaded data
  const getWarehouseName = (warehouseId, warehouseName = null) => {
    // Handle special case for mixed locations
    if (warehouseId === 'mixed' || warehouseName === 'Mixed') return 'Mixed';

    // Handle NULL/undefined warehouse_id (common for imported items)
    if (!warehouseId || warehouseId === 'null') return 'Unassigned';

    // Use the warehouse name if provided directly (from API response)
    if (warehouseName) return warehouseName;

    const warehouse = (contextWarehouses || allWarehouses || []).find(w => w.id === warehouseId);
    if (!warehouse && (contextWarehouses || allWarehouses || []).length > 0) {
      // Only warn for non-null warehouse IDs that we can't find
      console.warn('Warehouse not found:', warehouseId, 'Available warehouses:', (contextWarehouses || allWarehouses || []).map(w => ({id: w.id, name: w.name})));
    }
    return warehouse ? warehouse.name : 'Unknown Warehouse'; // Never show UUID
  };

  const getRackName = (rackId, rackName = null) => {
    // Handle special case for mixed locations
    if (rackId === 'mixed' || rackName === 'Mixed') return 'Mixed';

    if (!rackId) return '-';
    // Use the rack name if provided directly (from API response)
    if (rackName) return rackName;

    const rack = (contextRacks || allZones || []).find(r => r.id === rackId);
    return rack ? rack.name : 'Unknown Rack'; // Never show UUID
  };

  const getBayName = (bayId, bayName = null) => {
    // Handle special case for mixed locations
    if (bayId === 'mixed' || bayName === 'Mixed') return 'Mixed';

    if (!bayId) return '-';
    // Use the bay name if provided directly (from API response)
    if (bayName) return bayName;

    const bay = (contextBays || allBays || []).find(b => b.id === bayId);
    return bay ? bay.name : 'Unknown Bay'; // Never show UUID
  };
  
  // Status history dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // QR Code handler - directly print
  const handleShowQRCode = async (item) => {
    // Ensure job information is included for QR generation
    const itemWithJobInfo = {
      ...item,
      // Include job fields if the item is assigned to a job
      job_id: item.current_job_id || item.job_id,
      job_name: item.job_name || item.jobName,
      job_number: item.job_number || item.jobNumber,
      site_id: item.current_site_id || item.site_id,
      site_name: item.site_name || item.siteName
    };
    await printSingleQRCode(itemWithJobInfo);
  };

  // Grouping functions
  const createGroupKey = (item) => {
    // Decode HTML entities before creating the group key
    const decodedItem = decodeEquipmentItem(item);
    // Group by manufacturer, model, and description
    return `${decodedItem.manufacturer || 'Unknown'}-${decodedItem.model || 'Unknown'}-${decodedItem.description || 'Unknown'}`;
  };

  const groupInventoryData = useCallback((data) => {

    // Handle edge cases
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const groups = {};

    // Group items by manufacturer/model/description
    data.forEach(item => {
      const groupKey = createGroupKey(item);

      if (!groups[groupKey]) {
        // Decode HTML entities for the group
        const decodedItem = decodeEquipmentItem(item);
        groups[groupKey] = {
          groupKey,
          // Use first item's data as the representative
          manufacturer: decodedItem.manufacturer || 'Unknown',
          model: decodedItem.model || 'Unknown',
          description: decodedItem.description || 'Unknown',
          barcode: item.barcode, // Use first item's barcode
          category_id: item.category_id,
          category_name: item.category_name,
          // Aggregate data
          items: [],
          totalQuantity: 0,
          availableQuantity: 0,
          assignedQuantity: 0,
          // Location info - will be updated after all items are added
          warehouse: null,
          warehouse_id: null,
          warehouse_name: null,
          zone: null,
          zone_id: null,
          rack_id: null,
          rack_name: null,
          bay_id: null,
          bay_name: null,
          bay: null,
          // Track unique locations to detect mixed
          uniqueWarehouses: new Set(),
          uniqueRacks: new Set(),
          uniqueBays: new Set(),
          isGroup: true
        };
      }

      // Add item to group
      groups[groupKey].items.push(item);
      groups[groupKey].totalQuantity++;

      // Track unique locations
      if (item.warehouse_id) {
        groups[groupKey].uniqueWarehouses.add(item.warehouse_id);
      }
      if (item.rack_id) {
        groups[groupKey].uniqueRacks.add(item.rack_id);
      }
      if (item.bay_id) {
        groups[groupKey].uniqueBays.add(item.bay_id);
      }

      // Count quantities by status
      // Note: 'returned' items are semantically available (just need inspection)
      if (item.status === 'available' || item.status === 'returned') {
        groups[groupKey].availableQuantity++;
      } else if (item.status === 'assigned') {
        groups[groupKey].assignedQuantity++;
      }
    });

    // Process groups to set location info
    Object.values(groups).forEach(group => {
      // If only one unique value for each location field, use it
      // Otherwise, mark as "Mixed"

      // Handle warehouse
      if (group.uniqueWarehouses.size === 0) {
        // No warehouse set for any item
        group.warehouse_id = null;
        group.warehouse_name = null;
      } else if (group.uniqueWarehouses.size === 1) {
        // All items have the same warehouse
        const firstItem = group.items[0];
        group.warehouse_id = firstItem.warehouse_id;
        group.warehouse_name = firstItem.warehouse_name;
        group.warehouse = firstItem.warehouse;
      } else {
        // Mixed warehouses
        group.warehouse_id = 'mixed';
        group.warehouse_name = 'Mixed';
        group.warehouse = 'Mixed';
      }

      // Handle rack
      if (group.uniqueRacks.size === 0) {
        // No rack set for any item
        group.rack_id = null;
        group.rack_name = null;
        group.zone_id = null;
        group.zone = null;
      } else if (group.uniqueRacks.size === 1) {
        // All items have the same rack
        const firstItem = group.items[0];
        group.rack_id = firstItem.rack_id;
        group.rack_name = firstItem.rack_name;
        group.zone_id = firstItem.zone_id; // Legacy field
        group.zone = firstItem.zone; // Legacy field
      } else {
        // Mixed racks
        group.rack_id = 'mixed';
        group.rack_name = 'Mixed';
        group.zone_id = 'mixed';
        group.zone = 'Mixed';
      }

      // Handle bay
      if (group.uniqueBays.size === 0) {
        // No bay set for any item
        group.bay_id = null;
        group.bay_name = null;
        group.bay = null;
      } else if (group.uniqueBays.size === 1) {
        // All items have the same bay
        const firstItem = group.items[0];
        group.bay_id = firstItem.bay_id;
        group.bay_name = firstItem.bay_name;
        group.bay = firstItem.bay;
      } else {
        // Mixed bays
        group.bay_id = 'mixed';
        group.bay_name = 'Mixed';
        group.bay = 'Mixed';
      }

      // Clean up temporary tracking sets
      delete group.uniqueWarehouses;
      delete group.uniqueRacks;
      delete group.uniqueBays;
    });

    // Convert to array and sort by total quantity (highest first)
    const groupedArray = Object.values(groups).sort((a, b) => b.totalQuantity - a.totalQuantity);

    return groupedArray;
  }, []);

  // Handle group expansion
  const toggleGroupExpansion = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Get status breakdown for a group (counts per status)
  const getGroupStatusBreakdown = (group) => {
    if (!group.items || group.items.length === 0) return {};

    const statusCounts = {};
    group.items.forEach(item => {
      const status = item.status || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return statusCounts;
  };

  // Get unique statuses in workflow order
  const getOrderedStatuses = (statusCounts) => {
    const statusOrder = ['available', 'received', 'assigned', 'checked_out', 'installed', 'returned', 'maintenance', 'archived'];
    return statusOrder.filter(status => statusCounts[status] > 0);
  };

  // Sorting utility function
  const sortData = (data) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Check if we're sorting grouped data (has isGroup flag or items array)
      const isGroupedData = a.isGroup || (a.items && Array.isArray(a.items));

      // Handle special cases for warehouse and rack names
      if (sortField === 'warehouse') {
        aVal = getWarehouseName(a.warehouse_id, a.warehouse_name) || a.warehouse || '';
        bVal = getWarehouseName(b.warehouse_id, b.warehouse_name) || b.warehouse || '';
      } else if (sortField === 'rack') {
        aVal = getRackName(a.zone_id || a.rack_id, a.rack_name) || a.zone || '';
        bVal = getRackName(b.zone_id || b.rack_id, b.rack_name) || b.zone || '';
      } else if (sortField === 'bay') {
        aVal = getBayName(a.bay_id, a.bay_name) || a.bay || '';
        bVal = getBayName(b.bay_id, b.bay_name) || b.bay || '';
      } else if (sortField === 'received_at') {
        // For grouped data, use the first item's received date
        if (isGroupedData && a.items && a.items.length > 0) {
          aVal = new Date(a.items[0].received_at || a.items[0].created_at || a.items[0].timestamp || 0);
        } else {
          aVal = new Date(a.received_at || a.created_at || a.timestamp || 0);
        }
        if (isGroupedData && b.items && b.items.length > 0) {
          bVal = new Date(b.items[0].received_at || b.items[0].created_at || b.items[0].timestamp || 0);
        } else {
          bVal = new Date(b.received_at || b.created_at || b.timestamp || 0);
        }
      } else if (sortField === 'serial_number' || sortField === 'received_by') {
        // For grouped data, use first item's value for fields that don't exist on groups
        if (isGroupedData && a.items && a.items.length > 0) {
          aVal = a.items[0][sortField] || '';
        }
        if (isGroupedData && b.items && b.items.length > 0) {
          bVal = b.items[0][sortField] || '';
        }
      } else if (sortField === 'equipment_id') {
        // For grouped data, use first item's equipment_id
        if (isGroupedData && a.items && a.items.length > 0) {
          aVal = a.items[0].equipment_id || '';
        }
        if (isGroupedData && b.items && b.items.length > 0) {
          bVal = b.items[0].equipment_id || '';
        }
      }

      // Convert to strings for comparison (except dates)
      if (sortField === 'received_at') {
        // For dates, compare as numbers
        const aTime = aVal instanceof Date ? aVal.getTime() : 0;
        const bTime = bVal instanceof Date ? bVal.getTime() : 0;
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      } else {
        // For other fields, compare as strings
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();

        if (sortDirection === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }
    });
  };

  // Memoize the data processing function to avoid unnecessary recalculations
  const processInventoryData = useCallback((data) => {
    // Log data to understand what we're receiving
    
    // Unified map for all inventory items (both API and manual)
    const itemMap = {};
    
    // First, process any manual inventory items - ensure it's an array before iterating
    if (Array.isArray(manualInventoryItems)) {
      manualInventoryItems.forEach(item => {
        if (!item || !item.id) return; // Skip invalid items
        
        // Create a standardized entry for manual items
        // Decode HTML entities in the item first
        const decodedItem = decodeEquipmentItem(item);
       
        itemMap[item.id] = {
          id: item.id,
          description: decodedItem.description || [decodedItem.manufacturer, decodedItem.model].filter(p => p && p.trim()).join(' ') || 'Equipment Item',
          manufacturer: decodedItem.manufacturer || '',
          model: decodedItem.model || '',
          serial_number: item.serial_number || '',
          total_quantity: 1,
          available_quantity: (item.status === 'available' || item.status === 'returned') ? 1 : 0,
          warehouse: getWarehouseName(item.warehouse_id, item.warehouse_name) || item.warehouse || 'Unknown',
          warehouse_id: item.warehouse_id || '',
          warehouse_name: item.warehouse_name,
          zone: getRackName(item.zone_id, item.rack_name) || item.zone || '',
          zone_id: item.zone_id || '',
          rack_name: item.rack_name,
          current_job_id: item.current_job_id,
          current_site_id: item.current_site_id,
          last_updated: item.last_updated || item.timestamp || new Date().toISOString(),
          received_at: item.created_at || item.received_at || item.timestamp || item.last_updated, // Use available date fields
          received_by: item.received_by || '', // Add received_by field
          transactions: item.transactions || [],
          isManualItem: true,
          status: item.status || 'active'
        };
      });
    }
    
    // Process API data - now uses standardized V2 format
    if (Array.isArray(data)) {
      
      data.forEach(item => {
        if (!item || !item.id) return;
        
        // Skip if it's already in our manual items (manual items take precedence)
        if (itemMap[item.id] && itemMap[item.id].isManualItem) {
          return;
        }
        
        // Map V2 API format (individual equipment items)
        // Decode HTML entities in the item first
        const decodedApiItem = decodeEquipmentItem(item);
        
        itemMap[item.id] = {
          id: item.id,
          equipment_id: item.equipment_id || '', // Add missing equipment_id field
          description: decodedApiItem.description || decodedApiItem.name || '',
          manufacturer: decodedApiItem.manufacturer || '',
          model: decodedApiItem.model || '',
          serial_number: item.serial_number || '',
          barcode: item.barcode || '',
          warehouse: getWarehouseName(item.warehouse_id, item.warehouse_name) || item.warehouse || 'Unknown',
          warehouse_id: item.warehouse_id || '',
          warehouse_name: item.warehouse_name, // Preserve the name from API
          zone: getRackName(item.rack_id || item.zone_id, item.rack_name) || item.zone || '',
          zone_id: item.rack_id || item.zone_id || '',
          rack_name: item.rack_name, // Preserve the name from API
          bay_id: item.bay_id,
          bay_name: item.bay_name, // Preserve bay name from API
          bay: item.bay_name || item.bay, // Bay name/ID
          current_job_id: item.current_job_id,
          current_site_id: item.current_site_id,
          category_id: item.category || item.category_id,
          status: item.status || 'active',
          last_updated: item.updated_at || new Date().toISOString(),
          received_at: item.received_date || item.created_at || item.received_at || item.updated_at, // Use received_date (most recent warehouse receipt) or created_at as fallback
          received_by: item.received_by || '', // Add received_by field from API
          notes: item.notes || '', // Add notes field for display and editing
          vendor: item.vendor || '', // Add vendor field
          po_number: item.po_number || item.purchase_order || '', // Add PO number
          condition: item.condition || '' // Add condition field
        };
      });
    } else {
      // If data is not an array, just return the manual items
      return Object.values(itemMap);
    }
    
    // Convert to array, filter out zero quantity items, and sort by item code
    const allItems = Object.values(itemMap);
    
    const result = allItems
      .filter(item => {
        // For V2 individual items, check if they exist (no total_quantity field)
        if (item.total_quantity === undefined) {
          return true; // Individual items are always shown
        }
        // For legacy items with total_quantity
        return item.total_quantity > 0;
      })
      .sort((a, b) => {
        // Handle both string and numeric IDs
        const aId = String(a.id || '');
        const bId = String(b.id || '');
        return aId.localeCompare(bId);
      });
    
    return result;
  }, [manualInventoryItems]);

 
  const buildWarehouseFilterList = useCallback(() => {
    const warehouseNames = allWarehouses.map(w => w.name);
    setWarehouses(['All', ...warehouseNames]);
  }, [allWarehouses]);

  // Memoize the fetch function to avoid unnecessary re-renders
  const fetchInventoryData = useCallback(async (forceSearch = false, overrideShowArchived = null) => {
    const shouldIncludeArchived = overrideShowArchived !== null ? overrideShowArchived : showArchived;
    // Prevent rapid repeated fetches and infinite loops
    if ((isLoading && !forceSearch) || document.hidden) return;
    
    // Add a short debounce to prevent multiple rapid fetches
    if (window._lastFetchTime && (Date.now() - window._lastFetchTime < 2000) && !forceSearch) {
      return;
    }
    
    window._lastFetchTime = Date.now();
    setIsLoading(true);
    setDataError(null);
    
    try {
      let data;
      const currentSearchTerm = searchTerm.trim();
      
      // Always use the main equipment service, with or without search
      try {
        const params = {
          includeArchived: showArchived,
          limit: 20000,  // Fetch up to 20k items to ensure proper grouping with room to grow
          offset: (currentPage - 1) * itemsPerPage,
          sortBy: sortField || 'created_at',
          sortOrder: sortDirection || 'desc'
        };
        
        // Add search parameter if searching
        if (currentSearchTerm && forceSearch && currentSearchTerm.length > 0) {
          params.search = currentSearchTerm; // API expects 'search' not 'query'
        }
        
        const summaryData = await equipmentService.getEquipment(params);
        
        if (summaryData && summaryData.equipment) {
            data = summaryData.equipment; // Use V2 equipment array
            
            // Update pagination state from API response
            if (summaryData.pagination) {
              setTotalItems(summaryData.pagination.totalCount);
              setTotalPages(summaryData.pagination.pages);
            }
        } else {
          const legacyData = await loadInventorySummary({ includeArchived: showArchived });
          if (legacyData && legacyData.equipment) {
            data = legacyData.equipment;
          } else if (legacyData && (legacyData.All || legacyData.Main)) {
            data = legacyData;
          } else {
            data = await getSheetData();
          }
        }
      } catch (summaryErr) {
        console.warn('Error loading inventory summary, falling back to getSheetData:', summaryErr);
        data = await getSheetData();
      }
      
      // Debug log what we received
      
      // Debug: Log the structure to understand what's happening
      if (Array.isArray(data)) {
      } else if (typeof data === 'object' && data !== null) {
      }
      
      // Handle both array data (sheet data) and object data (summary)
      if (data) {
        let processedData;
        
        if (Array.isArray(data)) {
          // Check if it's V2 equipment items (has equipment_id field) or legacy sheet data
          if (data.length > 0 && data[0].equipment_id) {
            processedData = processInventoryData(data);
            
           
          } else {
            // It's legacy sheet data in array format
            const combinedData = data.concat(manualInventoryItems);
            processedData = processInventoryData(combinedData);
            
           
          }
        } else if (typeof data === 'object' && data.status === 'success' && Array.isArray(data.data)) {
          // Handle case where we received the full API response object instead of just the data
          const equipmentData = data.data;
          processedData = processInventoryData(equipmentData);
         
        } else if (typeof data === 'object' && (data.All || data.Main)) {
          // It's summary format
          processedData = processInventoryData(data);
          
          // Extract warehouses from summary data
          const warehouseNames = Object.keys(data);
          setWarehouses(['All', ...warehouseNames.filter(name => name !== 'All')]);
        } else {
          // Just use manual items
          processedData = processInventoryData(manualInventoryItems);
          
         
        }

        setInventoryData(processedData);
        
        // Group the data for display
        const grouped = groupInventoryData(processedData);
        setGroupedData(grouped);
        
        // Apply warehouse filter to grouped data
        let filtered = grouped;
        if (warehouse !== 'All') {
          filtered = filtered.filter(group => group.warehouse === warehouse);
        }
        setFilteredData(filtered);
        
        setLastUpdated(new Date());
        
        // Show success message
        if (showRefreshMessage) {
          setShowRefreshMessage(false);
          setTimeout(() => setShowRefreshMessage(true), 100);
        }
      } else {
        // Even if API data is invalid, show manual items
        const processedData = processInventoryData(manualInventoryItems);
        
        if (processedData && processedData.length > 0) {
          setInventoryData(processedData);
          
          // Apply warehouse filter
          let filtered = processedData;
          if (warehouse !== 'All') {
            filtered = filtered.filter(item => item.warehouse === warehouse);
          }
          setFilteredData(filtered);
          
         
          
          setLastUpdated(new Date());
        } else {
          console.error('No data to display after trying all data sources');
          setDataError('No inventory data available');
        }
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      
      // On error, still show manual items
      if (manualInventoryItems.length > 0) {
        const processedData = processInventoryData(manualInventoryItems);
        setInventoryData(processedData);
        
        // Apply warehouse filter
        let filtered = processedData;
        if (warehouse !== 'All') {
          filtered = filtered.filter(item => item.warehouse === warehouse);
        }
        setFilteredData(filtered);
        
        setLastUpdated(new Date());
        
        // Only show error if we can't connect to the API at all
        // If we're seeing this during normal receipt of items, don't show error
        const errorMessage = typeof err === 'string' ? err : 'Failed to fetch inventory data';
        if (errorMessage.includes('connection') || errorMessage.includes('network')) {
          setDataError(
            `Database connection error: ${errorMessage}. ` +
            `Showing ${manualInventoryItems.length} locally saved items.`
          );
        } else {
          // Clear any existing data error since we have manual items to show
          setDataError(null);
        }
      } else {
        setDataError(typeof err === 'string' ? err : 'Failed to fetch inventory data');
      }
      
      setIsLoading(false);
    }
  }, [searchTerm, warehouse, isLoading, sortField, sortDirection, itemsPerPage, currentPage, showArchived]); // Include all dependencies that affect the query

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      warehouse,
      searchTerm,
      selectedCategory,
      showArchived,
      showAvailableOnly,
      autoRefresh,
      sortField,
      sortDirection,
      itemsPerPage
    };
    localStorage.setItem('inventorySummarySettings', JSON.stringify(settings));
  }, [warehouse, searchTerm, selectedCategory, showArchived, showAvailableOnly, autoRefresh, sortField, sortDirection, itemsPerPage]);

  // Filter data based on warehouse and category selection
  useEffect(() => {
    let filtered = groupedData;

    // Apply warehouse filter
    if (warehouse !== 'All') {
      filtered = filtered.filter(group => group.warehouse === warehouse);
    }

    // Apply category filter
    // Only apply category filter if feature is enabled
    // Note: The filter state still exists, but won't have effect if UI is hidden
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(group => group.category_id == selectedCategory);
    }

    // Apply available only filter - show groups with at least one available item
    // When rendering, we'll filter individual items to show only available ones
    if (showAvailableOnly) {
      filtered = filtered.filter(group => group.availableQuantity > 0);

      // Filter items within each group to show only available items (including 'returned')
      filtered = filtered.map(group => ({
        ...group,
        items: group.items.filter(item => item.status === 'available' || item.status === 'returned'),
        totalQuantity: group.availableQuantity, // Update total to reflect filtered count
        assignedQuantity: 0 // No assigned items in filtered view
      }));
    }

    // Apply sorting to groups
    filtered = sortData(filtered);

    setFilteredData(filtered);
  }, [warehouse, selectedCategory, groupedData, sortField, sortDirection, showAvailableOnly]);

  // Simplified effect to just refresh when manual items change
  useEffect(() => {
    // If we have manual items, make sure to update state
    if (Array.isArray(manualInventoryItems) && manualInventoryItems.length > 0 && !isLoading) {
      console.debug(`Manual inventory items: ${manualInventoryItems.length}`);
      
      // Apply warehouse filter to manual items
      let filtered = [...manualInventoryItems]; // Create a new array to avoid mutation issues
      if (warehouse !== 'All') {
        filtered = filtered.filter(item => item.warehouse === warehouse);
      }
      
      // Ensure filtered data contains manual items
      setFilteredData(prev => {
        // If we don't have any filtered data yet, use manual items directly
        if (!Array.isArray(prev) || prev.length === 0) {
          return filtered;
        }
        
        try {
          // Otherwise merge them safely
          const existingMap = new Map();
          
          // First add existing items to the map
          prev.forEach(item => {
            if (item && item.id) {
              existingMap.set(item.id, item);
            }
          });
          
          // Then add filtered items
          filtered.forEach(item => {
            if (item && item.id) {
              existingMap.set(item.id, item);
            }
          });
          
          return Array.from(existingMap.values());
        } catch (error) {
          console.error('Error merging inventory data:', error);
          return filtered; // Fallback to just filtered data on error
        }
      });
    }
  }, [manualInventoryItems, warehouse, isLoading]);

  // Fetch categories on mount (only if feature is enabled)
  useEffect(() => {
    const fetchCategories = async () => {
      // Only fetch categories if the feature is enabled
      if (!isFeatureEnabled('EQUIPMENT_CATEGORIES')) {
        setCategories([]);
        return;
      }
      
      try {
        const response = await api.get('/categories?flat=true');
        setCategories(response.data.data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [isFeatureEnabled]);

  // Fetch warehouses and zones from API
  useEffect(() => {
    const fetchWarehousesAndZones = async () => {
      try {
        // Load warehouses
        const warehouseResponse = await api.get('/inventories/warehouses');
        
        // Handle nested structure: { success: true, data: { warehouses: [...] } }
        const warehouseData = warehouseResponse.data?.data?.warehouses || warehouseResponse.data?.data || warehouseResponse.data || [];
        
        const activeWarehouses = warehouseData.filter(w => w.isActive !== false);
        
        // Set all warehouses for edit dialog dropdown  
        setAllWarehouses(activeWarehouses);

        // Load zones
        const rackResponse = await api.get('/inventories/racks');
        const rackData = rackResponse.data?.data || rackResponse.data || [];
        setAllZones(rackData);
        
        // Load bays
        const bayResponse = await api.get('/inventories/bays');
        const bayData = bayResponse.data?.data || bayResponse.data || [];
        setAllBays(bayData);
        
      } catch (err) {
        console.error('Error fetching warehouses and zones:', err);
        console.error('Error details:', err.response?.data);
        console.error('Error status:', err.response?.status);
        // Keep existing data on error
      }
    };
    fetchWarehousesAndZones();
  }, []);

 
  useEffect(() => {
    buildWarehouseFilterList();
  }, [buildWarehouseFilterList]);

  // Load initial data only once
  useEffect(() => {
    // FIX: Directly call fetchInventoryData instead of relying on Context's loadInventorySummary
    // which was disabled to prevent repeated API calls (see InventoryContext.jsx lines 92-112)
    //
    // This ensures inventory loads immediately on page mount rather than waiting for
    // manual user interaction or context changes.
    console.log('InventorySummary: Initial mount - fetching inventory data');
    fetchInventoryData(true); // Force initial load, bypass debounce
  }, []); // Only run once on mount

  // Refresh data when lastUpdateTime changes (after adding items)
  useEffect(() => {
    if (lastUpdateTime) {
      fetchInventoryData(true);
    }
  }, [lastUpdateTime]);
  
  // Refresh data when showArchived changes
  useEffect(() => {
    // Clear cache to force fresh fetch
    window._lastFetchTime = 0;
    setInventoryData([]);
    setFilteredData([]);
    fetchInventoryData(true, showArchived);
  }, [showArchived]);

  // Reload data when pagination parameters change
  useEffect(() => {
    // Force fetch for pagination changes to bypass debounce
    window._lastFetchTime = 0; // Reset cache to force fetch
    fetchInventoryData(true); // Force the fetch
  }, [currentPage, itemsPerPage]);

  // Reload data when sorting parameters change
  useEffect(() => {
    if (sortField) {
      // Force fetch for sorting changes to bypass debounce
      window._lastFetchTime = 0; // Reset cache to force fetch
      fetchInventoryData(true); // Force the fetch
    }
  }, [sortField, sortDirection]);

  // Handle search - explicitly force a search
  const handleSearch = () => {
    // Pass true to force a search even if loading
    fetchInventoryData(true);
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchTerm('');
    // Reset pagination to first page
    setCurrentPage(1);
    // Clear any existing data to show loading state
    setInventoryData([]);
    setGroupedData([]);
    setFilteredData([]);
    // Force fetch without search
    window._lastFetchTime = 0;
    fetchInventoryData(false); // false = no search
  };

  // Handle warehouse change
  const handleWarehouseChange = (event) => {
    setWarehouse(event.target.value);
  };
  
  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };
  
  // Handle refresh message close
  const handleRefreshMessageClose = () => {
    setShowRefreshMessage(false);
  };

  // Edit/View handlers
  const handleEditItem = (item) => {
    debug('Editing item details:', item);
    debug('Available warehouses:', contextWarehouses || allWarehouses);
    debug('Available racks:', contextRacks || allZones);
    debug('Available bays:', contextBays);
    setEditingItem(item);
    setEditFormData({
      description: item.description || '',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serial_number: item.serial_number || '',
      warehouse: item.warehouse_id || '',
      rack: item.rack_id || item.zone_id || '', // Support both new and old field names
      bay: item.bay_id || item.bay || '',
      notes: item.notes || ''
    });
    
    // No need to load bays separately - they're already available from context
    
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditFormData({});
    setSaveError(null);
    setEditBays([]);
  };

  const handleEditFormChange = (field) => (event) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // No need to load bays separately - they're independent entities now
    // Just clear bay selection when warehouse or rack changes if needed
    if (field === 'warehouse' || field === 'rack') {
      // Optionally clear bay when warehouse/rack changes
      // setEditFormData(prev => ({ ...prev, bay: '' }));
    }
  };
  
  // Note: loadEditBays removed - bays are now loaded from context as independent entities

  const handleEditSave = async () => {
    try {
      setIsLoading(true);
      setSaveError(null);

      // Build update data, excluding empty string values for foreign key fields
      const updateData = {
        description: editFormData.description,
        manufacturer: editFormData.manufacturer,
        model: editFormData.model,
        serial_number: editFormData.serial_number,
        notes: editFormData.notes,
        updated_by: user?.email || 'system'
      };

      // Only include location fields if they have valid values (not empty strings)
      // Empty strings would cause foreign key constraint violations
      if (editFormData.warehouse && editFormData.warehouse !== '') {
        updateData.warehouse_id = editFormData.warehouse;
      }
      if (editFormData.rack && editFormData.rack !== '') {
        updateData.rack_id = editFormData.rack;
      }
      if (editFormData.bay && editFormData.bay !== '') {
        updateData.bay_id = editFormData.bay;
      }

      debug('Sending update data:', updateData);

      // Use the correct V2 API endpoint for updating equipment items
      const response = await api.put(`/equipment-items/${editingItem.id}`, updateData);

      setSaveSuccess(true);
      handleEditClose();
      // Refresh the inventory data
      fetchInventoryData(true);
    } catch (error) {
      console.error('Error updating equipment:', error);
      setSaveError(error.message);
    } finally {
      setIsLoading(false);
    }
  };


  const handleArchiveItem = async (item) => {
    // Check if item status is available or returned (both can be archived)
    if (item.status !== 'available' && item.status !== 'returned') {
      const statusMessages = {
        'assigned': 'currently assigned to a job',
        'checked_out': 'currently checked out',
        'installed': 'currently installed at a job site',
        'maintenance': 'currently in maintenance'
      };

      const statusMsg = statusMessages[item.status] || `in "${item.status}" status`;
      setDataError(`Cannot archive equipment that is ${statusMsg}. Only available or returned equipment can be archived.`);
      return;
    }

    if (window.confirm(`Are you sure you want to archive "${item.description || item.equipment_id}"? It will be hidden from the active inventory.`)) {
      try {
        setIsLoading(true);

        // Use the new dedicated archive endpoint
        const response = await api.post(`/equipment-items/${item.id}/archive`, {
          reason: 'Archived via inventory summary'
        });

        setSaveSuccess(true);
        // Clear any cached data and refresh
        setInventoryData([]);
        setFilteredData([]);
        // Clear cached timestamp to force refresh
        window._lastFetchTime = 0;
        // Also clear localStorage cache that might contain this item
        localStorage.removeItem('manualInventoryItems');
        // Force reload inventory summary from context
        if (loadInventorySummary) {
          await loadInventorySummary({ includeArchived: showArchived });
        }
        // Refresh the inventory data
        setTimeout(() => fetchInventoryData(true, showArchived), 100);
      } catch (error) {
        console.error('Error archiving equipment:', error);
        // Show user-friendly error message
        if (error.response?.data?.error) {
          setDataError(error.response.data.error);
          if (error.response.data.details) {
            setDataError(`${error.response.data.error}: ${error.response.data.details}`);
          }
        } else {
          setDataError(error.message);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleUnarchiveItem = async (item) => {
    if (window.confirm(`Are you sure you want to unarchive "${item.description || item.equipment_id}"? It will be restored to the active inventory.`)) {
      try {
        setIsLoading(true);

        // Use the new dedicated unarchive endpoint
        const response = await api.post(`/equipment-items/${item.id}/unarchive`);

        setSaveSuccess(true);
        // Clear any cached data and refresh
        setInventoryData([]);
        setFilteredData([]);
        // Clear cached timestamp to force refresh
        window._lastFetchTime = 0;
        // Also clear localStorage cache that might contain this item
        localStorage.removeItem('manualInventoryItems');
        // Force reload inventory summary from context
        if (loadInventorySummary) {
          await loadInventorySummary({ includeArchived: showArchived });
        }
        // Refresh the inventory data
        setTimeout(() => fetchInventoryData(true, showArchived), 100);
      } catch (error) {
        console.error('Error unarchiving equipment:', error);
        // Show user-friendly error message
        if (error.response?.data?.error) {
          setDataError(error.response.data.error);
          if (error.response.data.details) {
            setDataError(`${error.response.data.error}: ${error.response.data.details}`);
          }
        } else {
          setDataError(error.message);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRestoreItem = async (item) => {
    try {
      setIsLoading(true);

      // Use the new dedicated unarchive endpoint
      const response = await api.post(`/equipment-items/${item.id}/unarchive`);

      setSaveSuccess(true);
      // Clear any cached data and refresh
      setInventoryData([]);
      setFilteredData([]);
      // Clear cached timestamp to force refresh
      window._lastFetchTime = 0;
      // Also clear localStorage cache that might contain this item
      localStorage.removeItem('manualInventoryItems');
      // Force reload inventory summary from context
      if (loadInventorySummary) {
        await loadInventorySummary({ includeArchived: showArchived });
      }
      // Refresh the inventory data
      setTimeout(() => fetchInventoryData(true, showArchived), 100);
    } catch (error) {
      console.error('Error restoring equipment:', error);
      setDataError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Get all archivable items (available or returned status only)
      const allArchivableIds = groupedData.flatMap(group =>
        (group.items || [])
          .filter(item => item.status === 'available' || item.status === 'returned')
          .map(item => item.id)
      );
      setSelectedItems(allArchivableIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleBulkArchive = async () => {
    if (selectedItems.length === 0) {
      setDataError('No items selected for archiving');
      return;
    }

    if (window.confirm(`Are you sure you want to archive ${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''}? They will be hidden from the active inventory.`)) {
      try {
        setIsLoading(true);

        // Use the bulk archive endpoint from equipment service
        const response = await equipmentService.bulkArchiveEquipment(selectedItems, 'Bulk archive via inventory summary');

        setSaveSuccess(true);
        setSelectedItems([]); // Clear selection

        // Clear any cached data and refresh
        setInventoryData([]);
        setFilteredData([]);
        window._lastFetchTime = 0;
        localStorage.removeItem('manualInventoryItems');

        // Force reload inventory summary from context
        if (loadInventorySummary) {
          await loadInventorySummary({ includeArchived: showArchived });
        }

        // Refresh the inventory data
        setTimeout(() => fetchInventoryData(true, showArchived), 100);
      } catch (error) {
        console.error('Error bulk archiving equipment:', error);
        if (error.response?.data?.error) {
          setDataError(error.response.data.error);
        } else {
          setDataError('Failed to archive equipment items. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle showing item history
  const handleShowHistory = (item) => {
    setHistoryItem(item);
    setHistoryDialogOpen(true);
  };

  const handleHistoryClose = () => {
    setHistoryDialogOpen(false);
    setHistoryItem(null);
  };

  // Use mobile component on small screens
  if (isMobile) {
    return (
      <>
        <InventorySummaryMobile
          inventory={filteredData}
          searchQuery={searchTerm}
          onSearchChange={setSearchTerm}
          onEdit={handleEditItem}
          onArchive={handleArchiveItem}
          onUnarchive={handleUnarchiveItem}
          isAdmin={isAdmin}
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          loading={isLoading}
        />
      </>
    );
  }

  // Desktop view
  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" component="h2">
          Current Inventory
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {lastUpdated && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          
          <Tooltip title="Refresh inventory data">
            <IconButton 
              color="primary" 
              onClick={fetchInventoryData} 
              disabled={isLoading}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Search and filter controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          label="Search Inventory"
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            // Trigger search automatically after a short delay
            clearTimeout(window.searchTimeout);
            window.searchTimeout = setTimeout(() => {
              fetchInventoryData(true);
            }, 300); // 300ms delay to avoid too many API calls
          }}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ maxWidth: '70%' }}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {searchTerm && (
                  <IconButton 
                    size="small" 
                    onClick={handleClearSearch}
                    disabled={isLoading}
                    title="Clear search"
                  >
                    <ClearIcon />
                  </IconButton>
                )}
                <IconButton 
                  size="small" 
                  onClick={handleSearch}
                  disabled={isLoading}
                  title="Search"
                >
                  <SearchIcon />
                </IconButton>
              </Box>
            ),
          }}
        />
        
        <FeatureGate feature="ENHANCED_SEARCH">
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="warehouse-select-label">Warehouse</InputLabel>
            <Select
              labelId="warehouse-select-label"
              id="warehouse-select"
              value={warehouse}
              label="Warehouse"
              onChange={handleWarehouseChange}
            >
              {warehouses.map((w) => (
                <MenuItem key={w} value={w}>{w}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FeatureGate>
        
        <FeatureGate feature="EQUIPMENT_CATEGORIES">
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel id="category-select-label">Category</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="All">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FeatureGate>

        <FormControlLabel
          control={
            <Switch
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              color="primary"
            />
          }
          label="Show Archived"
        />

        <FormControlLabel
          control={
            <Switch
              checked={showAvailableOnly}
              onChange={(e) => setShowAvailableOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Available Only"
        />
      </Box>
      
      {/* Pagination and Items Per Page Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Items per page:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 70 }}>
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(e.target.value);
                setCurrentPage(1); // Reset to first page when changing items per page
              }}
            >
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {totalItems > 0 && (
              filteredData.length > 0
                ? `Showing ${filteredData.length} groups (${filteredData.reduce((sum, g) => sum + (g.totalQuantity || 1), 0)} items) of ${totalItems} total items`
                : `Showing ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems} items`
            )}
          </Typography>
          {/* Bulk Archive Button - only show when items are selected and user is admin */}
          {selectedItems.length > 0 && isAdmin && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<ArchiveIcon />}
              onClick={handleBulkArchive}
              disabled={isLoading}
            >
              Archive {selectedItems.length} Item{selectedItems.length > 1 ? 's' : ''}
            </Button>
          )}
        </Stack>

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
            color="primary"
            size="small"
          />
        )}
      </Box>
      
      {/* Error handling */}
      {dataError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDataError(null)}>
          {dataError}
        </Alert>
      )}
      
      {/* Show warning when only manual items are available AND there's a data error */}
      {dataError && manualInventoryItems.length > 0 && filteredData.length > 0 && 
       filteredData.every(item => item.isManualItem) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Currently showing only manually added inventory items. Database data could not be loaded. 
          Your items have been saved locally and will be synced to the database when connection is restored.
        </Alert>
      )}
      
      {/* Loading state */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Data display */}
          {(!filteredData || filteredData.length === 0) && (!manualInventoryItems || manualInventoryItems.length === 0) ? (
            <Alert severity="info">
              No inventory items found. {searchTerm && 'Try adjusting your search criteria.'}
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Click on any row to view the equipment's status history
                </Typography>
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '70vh', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: { xs: '100%', sm: 650 } }} aria-label="inventory table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 50 }}>
                      {/* Expand/Collapse column */}
                    </TableCell>
                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < groupedData.flatMap(g => (g.items || []).filter(item => item.status === 'available' || item.status === 'returned')).length}
                        checked={groupedData.flatMap(g => (g.items || []).filter(item => item.status === 'available' || item.status === 'returned')).length > 0 && selectedItems.length === groupedData.flatMap(g => (g.items || []).filter(item => item.status === 'available' || item.status === 'returned')).length}
                        onChange={handleSelectAll}
                        disabled={groupedData.flatMap(g => (g.items || []).filter(item => item.status === 'available' || item.status === 'returned')).length === 0}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('barcode')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Barcode
                        {sortField === 'barcode' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none', display: { xs: 'none', sm: 'table-cell' } }}
                      onClick={() => handleSort('manufacturer')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Manufacturer
                        {sortField === 'manufacturer' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none', display: { xs: 'none', md: 'table-cell' } }}
                      onClick={() => handleSort('model')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Model
                        {sortField === 'model' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('description')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Description
                        {sortField === 'description' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none', display: { xs: 'none', lg: 'table-cell' } }}
                      onClick={() => handleSort('serial_number')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Serial Number
                        {sortField === 'serial_number' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none', display: { xs: 'none', md: 'table-cell' } }}
                      onClick={() => handleSort('received_at')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Date Received
                        {sortField === 'received_at' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Received By</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('totalQuantity')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Quantity
                        {sortField === 'totalQuantity' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('status')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Status
                        {sortField === 'status' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Current Job</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('warehouse')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Warehouse
                        {sortField === 'warehouse' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('rack')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Rack
                        {sortField === 'rack' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('bay')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Bay
                        {sortField === 'bay' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('equipment_id')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Equipment ID
                        {sortField === 'equipment_id' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('notes')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Notes
                        {sortField === 'notes' && (
                          sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Display grouped data with expandable rows */}
                  {filteredData.map((group) => {
                    const isExpanded = expandedGroups.has(group.groupKey);
                    const hasMultipleItems = group.totalQuantity > 1;
                    
                    return (
                      <React.Fragment key={group.groupKey}>
                        {/* Group header row */}
                        <TableRow 
                          hover
                          sx={{ 
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (hasMultipleItems) {
                              toggleGroupExpansion(group.groupKey);
                            } else {
                              // For single items, show history directly
                              handleShowHistory(group.items[0]);
                            }
                          }}
                        >
                          {/* Expand/Collapse button */}
                          <TableCell>
                            {hasMultipleItems ? (
                              <IconButton size="small">
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            ) : null}
                          </TableCell>

                          {/* Checkbox for selection (only for archivable items) */}
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            {/* Only show checkbox if at least one item in group is archivable */}
                            {group.items && group.items.length > 0 && group.items.some(item => item.status === 'available' || item.status === 'returned') && (
                              <Checkbox
                                checked={group.items.filter(item => item.status === 'available' || item.status === 'returned').every(item => selectedItems.includes(item.id))}
                                indeterminate={group.items.filter(item => item.status === 'available' || item.status === 'returned').some(item => selectedItems.includes(item.id)) && !group.items.filter(item => item.status === 'available' || item.status === 'returned').every(item => selectedItems.includes(item.id))}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const archivableItems = group.items.filter(item => item.status === 'available' || item.status === 'returned');
                                  const archivableIds = archivableItems.map(item => item.id);
                                  const allSelected = archivableItems.every(item => selectedItems.includes(item.id));

                                  if (allSelected) {
                                    // Deselect all items in this group
                                    setSelectedItems(prev => prev.filter(id => !archivableIds.includes(id)));
                                  } else {
                                    // Select all archivable items in this group
                                    setSelectedItems(prev => [...new Set([...prev, ...archivableIds])]);
                                  }
                                }}
                              />
                            )}
                          </TableCell>

                          {/* Barcode - show first item's barcode */}
                          <TableCell>{group.barcode || '-'}</TableCell>

                          {/* Manufacturer */}
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{group.manufacturer}</TableCell>

                          {/* Model */}
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{group.model}</TableCell>

                          {/* Description */}
                          <TableCell>{group.description}</TableCell>
                          
                          {/* Serial Number - show count if multiple */}
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            {hasMultipleItems ? `${group.totalQuantity} items` : (group.items && group.items[0]?.serial_number) || '-'}
                          </TableCell>
                          
                          {/* Date Received - show first item's date */}
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {(group.items && group.items[0]?.received_at) ? new Date(group.items[0].received_at).toLocaleDateString() : '-'}
                          </TableCell>
                          
                          {/* Received By */}
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            {(group.items && group.items[0]?.received_by) || '-'}
                          </TableCell>
                          
                          {/* Quantity */}
                          <TableCell>
                            <Chip 
                              label={group.totalQuantity}
                              color={hasMultipleItems ? "primary" : "default"}
                              size="small"
                            />
                          </TableCell>
                          
                          {/* Status - show circles for each status */}
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              {(() => {
                                const statusCounts = getGroupStatusBreakdown(group);
                                const orderedStatuses = getOrderedStatuses(statusCounts);

                                return orderedStatuses.map(status => {
                                  const statusInfo = getStatusDisplay(status);
                                  const count = statusCounts[status];

                                  return (
                                    <Tooltip
                                      key={status}
                                      title={`${count} ${statusInfo.label}`}
                                      arrow
                                    >
                                      <Box
                                        sx={{
                                          width: 12,
                                          height: 12,
                                          borderRadius: '50%',
                                          backgroundColor: statusInfo.color,
                                          flexShrink: 0
                                        }}
                                      />
                                    </Tooltip>
                                  );
                                });
                              })()}
                            </Box>
                          </TableCell>
                          
                          {/* Current Job - show if single item or summary */}
                          <TableCell>
                            {hasMultipleItems ? 
                              (group.assignedQuantity > 0 ? `${group.assignedQuantity} assigned` : '-') :
                              ((group.items && group.items[0]?.current_job_id) || '-')
                            }
                          </TableCell>
                          
                          {/* Warehouse */}
                          <TableCell>{getWarehouseName(group.warehouse_id, group.warehouse_name)}</TableCell>
                          
                          {/* Zone/Rack */}
                          <TableCell>{getRackName(group.zone_id || group.rack_id, group.rack_name)}</TableCell>
                          
                          {/* Bay */}
                          <TableCell>{getBayName(group.bay_id, group.bay_name)}</TableCell>
                          
                          {/* Equipment ID - show count if multiple */}
                          <TableCell>
                            {hasMultipleItems ? `${group.totalQuantity} items` : (group.items && group.items[0]?.equipment_id || '-')}
                          </TableCell>
                          
                          {/* Notes - truncated with tooltip */}
                          <TableCell>
                            {!hasMultipleItems && group.items && group.items[0] && group.items[0].notes ? (
                              <Tooltip 
                                title={
                                  <LinkifiedText 
                                    text={group.items[0].notes}
                                    typographyProps={{ 
                                      variant: "body2",
                                      sx: { color: 'inherit' }
                                    }}
                                  />
                                } 
                                arrow
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 150,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {group.items[0].notes}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.disabled">-</Typography>
                            )}
                          </TableCell>

                          {/* Actions - only for single items */}
                          <TableCell>
                            {!hasMultipleItems && group.items && group.items[0] && (
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View QR Code">
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowQRCode(group.items[0]);
                                    }}
                                  >
                                    <QrCodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isAdmin && (
                                  <>
                                    <Tooltip title="Edit item">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditItem(group.items[0]);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {group.items[0].status === 'archived' ? (
                                      <Tooltip title="Unarchive item">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnarchiveItem(group.items[0]);
                                          }}
                                        >
                                          <UnarchiveIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Archive item">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleArchiveItem(group.items[0]);
                                          }}
                                        >
                                          <ArchiveIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </Box>
                            )}
                            {hasMultipleItems && (
                              <Typography variant="body2" color="text.secondary">
                                Expand to see individual items
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded individual items */}
                        {hasMultipleItems && isExpanded && group.items && group.items.map((item) => (
                          <TableRow
                            key={item.id}
                            sx={{
                              backgroundColor: 'action.selected',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                            onClick={() => handleShowHistory(item)}
                          >
                            {/* Empty expand cell */}
                            <TableCell sx={{ pl: 4 }}>↳</TableCell>

                            {/* Checkbox for individual item (only for archivable items) */}
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              {(item.status === 'available' || item.status === 'returned') && (
                                <Checkbox
                                  checked={selectedItems.includes(item.id)}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectItem(item.id);
                                  }}
                                />
                              )}
                            </TableCell>

                            {/* Barcode */}
                            <TableCell>{item.barcode || '-'}</TableCell>

                            {/* Manufacturer */}
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{item.manufacturer}</TableCell>

                            {/* Model */}
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{item.model}</TableCell>

                            {/* Description - indent to show hierarchy */}
                            <TableCell sx={{ pl: 2, fontStyle: 'italic' }}>
                              {item.description}
                            </TableCell>
                            
                            {/* Serial Number */}
                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{item.serial_number || '-'}</TableCell>
                            
                            {/* Date Received */}
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {item.received_at ? new Date(item.received_at).toLocaleDateString() : '-'}
                            </TableCell>
                            
                            {/* Received By */}
                            <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{item.received_by || '-'}</TableCell>
                            
                            {/* Quantity - always 1 for individual items */}
                            <TableCell>1</TableCell>
                            
                            {/* Status */}
                            <TableCell>
                              <Chip
                                label={getStatusDisplay(item.status).label}
                                size="small"
                                sx={{
                                  backgroundColor: getStatusDisplay(item.status).color,
                                  color: 'white'
                                }}
                              />
                            </TableCell>
                            
                            {/* Current Job */}
                            <TableCell>{item.current_job_id || '-'}</TableCell>
                            
                            {/* Warehouse */}
                            <TableCell>{getWarehouseName(item.warehouse_id, item.warehouse_name)}</TableCell>
                            
                            {/* Zone/Rack */}
                            <TableCell>{getRackName(item.zone_id || item.rack_id, item.rack_name)}</TableCell>
                            
                            {/* Bay */}
                            <TableCell>{getBayName(item.bay_id, item.bay_name)}</TableCell>
                            
                            {/* Equipment ID */}
                            <TableCell>{item.equipment_id}</TableCell>
                            
                            {/* Notes */}
                            <TableCell>
                              {item.notes ? (
                                <Tooltip 
                                  title={
                                    <LinkifiedText 
                                      text={item.notes}
                                      typographyProps={{ 
                                        variant: "body2",
                                        sx: { color: 'inherit' }
                                      }}
                                    />
                                  } 
                                  arrow
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      maxWidth: 150,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {item.notes}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.disabled">-</Typography>
                              )}
                            </TableCell>
                            
                            {/* Actions for individual items */}
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View QR Code">
                                  <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowQRCode(item);
                                    }}
                                  >
                                    <QrCodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isAdmin && (
                                  <>
                                    <Tooltip title="Edit item">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditItem(item);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {item.status === 'archived' ? (
                                      <Tooltip title="Unarchive item">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnarchiveItem(item);
                                          }}
                                        >
                                          <UnarchiveIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Archive item">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleArchiveItem(item);
                                          }}
                                        >
                                          <ArchiveIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredData.length} of {inventoryData.length} equipment
              </Typography>
              
              <Button 
                variant="outlined" 
                size="small" 
                onClick={fetchInventoryData}
                startIcon={<RefreshIcon />}
              >
                Refresh Data
              </Button>
            </Box>
          </Box>
        </>
      )}
      
      {/* Refresh notification */}
      <Snackbar
        open={showRefreshMessage}
        autoHideDuration={6000}
        onClose={handleRefreshMessageClose}
        message="New inventory items available! Click refresh to update the view."
        action={
          <Button 
            color="primary" 
            size="small" 
            onClick={fetchInventoryData}
          >
            Refresh Now
          </Button>
        }
      />
      
      {/* Equipment Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Equipment: {editingItem?.equipment_id || editingItem?.id}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Read-only fields */}
            <TextField
              label="Equipment ID"
              value={editingItem?.equipment_id || editingItem?.id || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />
            <TextField
              label="Description"
              value={editFormData.description || ''}
              onChange={handleEditFormChange('description')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter equipment description"
            />
            <TextField
              label="Barcode"
              value={editingItem?.barcode || 'Not specified'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />
            <TextField
              label="Status"
              value={editingItem?.status || 'Unknown'}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />

            {/* Editable fields */}
            <TextField
              label="Manufacturer"
              value={editFormData.manufacturer || ''}
              onChange={handleEditFormChange('manufacturer')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter manufacturer"
            />
            <TextField
              label="Model"
              value={editFormData.model || ''}
              onChange={handleEditFormChange('model')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter model"
            />
            <TextField
              label="Serial Number"
              value={editFormData.serial_number || ''}
              onChange={handleEditFormChange('serial_number')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter serial number"
            />

            {/* Location fields - dropdowns */}
            <FormControl fullWidth size="small">
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={editFormData.warehouse || ''}
                onChange={handleEditFormChange('warehouse')}
                label="Warehouse"
                disabled={(contextWarehouses || allWarehouses || []).length === 0}
              >
                <MenuItem value="">
                  <em>Not assigned</em>
                </MenuItem>
                {(contextWarehouses || allWarehouses || []).length === 0 ? (
                  <MenuItem disabled>
                    <em>Loading warehouses...</em>
                  </MenuItem>
                ) : (
                  (contextWarehouses || allWarehouses || []).map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </MenuItem>
                  ))
                )}
                {/* Show current value if not found in loaded warehouses */}
                {editFormData.warehouse && !(contextWarehouses || allWarehouses || []).find(w => w.id === editFormData.warehouse) && (
                  <MenuItem key={editFormData.warehouse} value={editFormData.warehouse}>
                    Unknown Warehouse
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Rack</InputLabel>
              <Select
                value={editFormData.rack || ''}
                onChange={handleEditFormChange('rack')}
                label="Rack"
                disabled={(contextRacks || allZones || []).length === 0}
              >
                <MenuItem value="">
                  <em>Not assigned</em>
                </MenuItem>
                {(contextRacks || allZones || []).length === 0 ? (
                  <MenuItem disabled>
                    <em>Loading racks...</em>
                  </MenuItem>
                ) : (
                  (contextRacks || allZones || [])
                    .sort((a, b) => {
                      const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
                      const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
                      return aNum - bNum;
                    })
                    .map((rack) => (
                      <MenuItem key={rack.id} value={rack.id}>
                        {rack.name}
                      </MenuItem>
                    ))
                )}
                {/* Show current value if not found in loaded racks */}
                {editFormData.rack && !(contextRacks || allZones || []).find(r => r.id === editFormData.rack) && (
                  <MenuItem key={editFormData.rack} value={editFormData.rack}>
                    Unknown Rack
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Bay</InputLabel>
              <Select
                value={editFormData.bay || ''}
                onChange={handleEditFormChange('bay')}
                label="Bay"
                disabled={(contextBays || editBays || []).length === 0}
              >
                <MenuItem value="">
                  <em>Not assigned</em>
                </MenuItem>
                {(contextBays || editBays || []).length === 0 ? (
                  <MenuItem disabled>
                    <em>Loading bays...</em>
                  </MenuItem>
                ) : (
                  (contextBays || editBays || [])
                    .sort((a, b) => {
                      const aNum = parseInt(a.name.match(/\d+/)?.[0] || '0');
                      const bNum = parseInt(b.name.match(/\d+/)?.[0] || '0');
                      return aNum - bNum;
                    })
                    .map((bay) => (
                      <MenuItem key={bay.id} value={bay.id}>
                        {bay.name}
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>

            {/* Notes field - multiline for better tracking */}
            <TextField
              label="Notes"
              value={editFormData.notes || ''}
              onChange={handleEditFormChange('notes')}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder="Track delays, issues, special instructions..."
              helperText="Document equipment status, delays, or special instructions"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        message="Equipment updated successfully"
      />

      {/* Status History Dialog */}
      <StatusHistoryDialog
        open={historyDialogOpen}
        onClose={handleHistoryClose}
        equipment={historyItem}
        onEquipmentUpdate={(updatedEquipment) => {
          // Update the equipment in the local inventory list
          setInventoryData(prev => {
            const updatedList = prev.map(item =>
              item.id === updatedEquipment.id ? updatedEquipment : item
            );

            // Re-group the data to update the UI
            const newGroupedData = groupInventoryData(updatedList);
            setGroupedData(newGroupedData);

            return updatedList;
          });
        }}
      />

    </Paper>
  </>
  );
}